'use client';

import type { ReactNode } from 'react';
import type { CustomerAmount } from '@packages/gql/generated/shopping/graphql';
import { FormattedMessage } from 'react-intl';
import { Price } from '@packages/shared/src/ui/Price/Price';
import { PriceValue } from '@packages/shared/src/ui/Price/PriceValue/PriceValue';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { Chip } from '@packages/shared/src/ui/Chip/Chip';

type AccountBalanceSummaryProps = {
  label: ReactNode;
  balance: CustomerAmount;
};

// Message ids stayed under the `Maturities.*` namespace they were introduced with
// (Maturities and PartialPaymentCard had duplicated this row with separately
// translated ids — reusing these avoids re-translating across all 14 tenants).
export const AccountBalanceSummary = ({ label, balance }: AccountBalanceSummaryProps) => {
  const { formatPrice } = useFormatPrice();

  return (
    <div className="flex justify-between">
      <div className="font-bold">{label}</div>
      <div>
        <Price size="small" alignment="right">
          <PriceValue>{formatPrice(balance.value, balance.currencyCode)}</PriceValue>
        </Price>
        {balance.value < 0 && (
          <Chip variant="filled" size="small" color="error" className="ml-2">
            <span className="px-1 py-0.25">
              <FormattedMessage id="Maturities.BalanceNegative" defaultMessage="OFFENER SALDO" />
            </span>
          </Chip>
        )}
        {balance.value > 0 && (
          <Chip variant="filled" size="small" color="success" className="ml-2">
            <span className="px-1 py-0.25">
              <FormattedMessage id="Maturities.BalancePositive" defaultMessage="Guthaben" />
            </span>
          </Chip>
        )}
        {balance.value === 0 && (
          <div className="font-bold">
            <FormattedMessage
              id="Maturities.BalanceZero"
              defaultMessage="Dein Konto ist ausgeglichen"
            />
          </div>
        )}
      </div>
    </div>
  );
};
